import React, { useState, useEffect, useMemo, forwardRef, useImperativeHandle } from 'react';

const TimeRulesTab = forwardRef(function TimeRulesTab({ config, onUpdate, hideWizardSaveBar = false }, ref) {
  const [formData, setFormData] = useState({
    campaignStart: '',
    campaignEnd: '',
    receiptDateBuffer: 3
  });
  const [saving, setSaving] = useState(false);
  const [message, setMessage] = useState(null);
  const [dateError, setDateError] = useState(null);

  const isDirty = useMemo(() => {
    const tr = config?.timeRules || {};
    const startSaved = tr.campaignStart || '';
    const endSaved = tr.campaignEnd || '';
    const bufSaved = tr.receiptDateBuffer ?? 3;
    const startForm = formData.campaignStart || '';
    const endForm = formData.campaignEnd || '';
    if (startForm !== startSaved || endForm !== endSaved) return true;
    if (formData.receiptDateBuffer === '') return true;
    const bufNum = parseInt(formData.receiptDateBuffer, 10);
    if (!Number.isFinite(bufNum)) return true;
    return bufNum !== bufSaved;
  }, [config?.timeRules, formData.campaignStart, formData.campaignEnd, formData.receiptDateBuffer]);

  useEffect(() => {
    if (config?.timeRules) {
      setFormData({
        campaignStart: config.timeRules.campaignStart || '',
        campaignEnd: config.timeRules.campaignEnd || '',
        receiptDateBuffer: config.timeRules.receiptDateBuffer ?? 3
      });
    }
  }, [config]);

  // Validate dates whenever they change
  useEffect(() => {
    if (formData.campaignStart && formData.campaignEnd) {
      if (formData.campaignEnd < formData.campaignStart) {
        setDateError('End date cannot be earlier than start date');
      } else {
        setDateError(null);
      }
    } else {
      setDateError(null);
    }
  }, [formData.campaignStart, formData.campaignEnd]);

  const handleBufferChange = (value) => {
    // Allow empty value for editing
    if (value === '') {
      setFormData({ ...formData, receiptDateBuffer: '' });
      return;
    }
    
    // Only allow digits
    const cleanValue = value.replace(/[^\d]/g, '');
    
    // Remove leading zeros but keep at least one digit
    const noLeadingZeros = cleanValue.replace(/^0+/, '') || '0';
    
    const numValue = parseInt(noLeadingZeros, 10) || 0;
    
    setFormData({ ...formData, receiptDateBuffer: numValue });
  };

  const handleSave = async () => {
    // Validate dates before saving
    if (dateError) {
      setMessage({ type: 'error', text: dateError });
      return;
    }
    
    setSaving(true);
    setMessage(null);
    try {
      // Ensure receiptDateBuffer is a number
      const bufferValue = parseInt(formData.receiptDateBuffer, 10) || 0;
      
      await onUpdate({ 
        timeRules: {
          campaignStart: formData.campaignStart || null,
          campaignEnd: formData.campaignEnd || null,
          receiptDateBuffer: bufferValue
        }
      });
      setMessage({ type: 'success', text: 'Time rules saved successfully!' });
      setTimeout(() => setMessage(null), 3000);
    } catch (error) {
      setMessage({ type: 'error', text: 'Failed to save changes. Please try again.' });
    } finally {
      setSaving(false);
    }
  };

  useImperativeHandle(ref, () => ({
    saveStep: async () => {
      if (dateError) {
        return { ok: false, error: dateError };
      }
      setSaving(true);
      setMessage(null);
      try {
        const bufferValue = parseInt(formData.receiptDateBuffer, 10) || 0;
        await onUpdate({
          timeRules: {
            campaignStart: formData.campaignStart || null,
            campaignEnd: formData.campaignEnd || null,
            receiptDateBuffer: bufferValue
          }
        });
        return { ok: true };
      } catch {
        return { ok: false, error: 'Failed to save time rules.' };
      } finally {
        setSaving(false);
      }
    }
  }), [dateError, formData, onUpdate]);

  const formatDate = (dateStr) => {
    if (!dateStr) return 'Not set';
    return new Date(dateStr).toLocaleDateString('en-MY', {
      weekday: 'long',
      year: 'numeric',
      month: 'long',
      day: 'numeric'
    });
  };

  // Helper to parse date string as LOCAL time (not UTC)
  const parseLocalDate = (dateString) => {
    if (!dateString) return null;
    const [year, month, day] = dateString.split('T')[0].split('-').map(Number);
    return new Date(year, month - 1, day, 0, 0, 0, 0);
  };
  
  // Helper to get end of day (23:59:59.999) for a date in LOCAL time
  const getEndOfDay = (dateString) => {
    if (!dateString) return null;
    const [year, month, day] = dateString.split('T')[0].split('-').map(Number);
    return new Date(year, month - 1, day, 23, 59, 59, 999);
  };

  const getCampaignStatus = () => {
    if (!formData.campaignStart || !formData.campaignEnd) return null;
    
    const now = new Date();
    const start = parseLocalDate(formData.campaignStart);
    // End date should include the full day (until 23:59:59 local time)
    const end = getEndOfDay(formData.campaignEnd);
    
    // Buffer period applies to BOTH start and end dates
    const bufferDays = parseInt(formData.receiptDateBuffer, 10) || 0;
    const bufferMs = bufferDays * 24 * 60 * 60 * 1000;
    const startWithBuffer = new Date(start.getTime() - bufferMs);
    const endWithBuffer = new Date(end.getTime() + bufferMs);
    
    if (now < startWithBuffer) return { status: 'upcoming', label: 'Upcoming', color: '#3b82f6' };
    if (now < start) return { status: 'pre-buffer', label: 'Pre-Campaign Buffer', color: '#f59e0b' };
    if (now > endWithBuffer) return { status: 'ended', label: 'Ended', color: '#ef4444' };
    if (now > end) return { status: 'post-buffer', label: 'Post-Campaign Buffer', color: '#f59e0b' };
    return { status: 'active', label: 'Active', color: '#10b981' };
  };

  const campaignStatus = getCampaignStatus();

  return (
    <div className="tab-panel">
      <div className="tab-header">
        <h2>Time Rules</h2>
        <p>Control the valid time window for claims and receipt dates.</p>
      </div>

      <div className="rules-form">
        <div className="form-section">
          <div className="form-section-title">Campaign Period</div>
          <p style={{ color: '#64748b', fontSize: '0.875rem', margin: '0 0 1rem 0' }}>
            Claims can only be submitted during the active campaign period.
          </p>
          
          <div className="date-range">
            <div className="form-group">
              <label>Start Date</label>
              <input
                type="date"
                value={formData.campaignStart}
                onChange={(e) => setFormData({ ...formData, campaignStart: e.target.value })}
              />
            </div>
            <span className="date-separator">to</span>
            <div className="form-group">
              <label>End Date</label>
              <input
                type="date"
                value={formData.campaignEnd}
                min={formData.campaignStart || undefined}
                onChange={(e) => setFormData({ ...formData, campaignEnd: e.target.value })}
                style={dateError ? { borderColor: '#ef4444' } : {}}
              />
            </div>
          </div>
          
          {dateError && (
            <div style={{ 
              marginTop: '0.5rem',
              padding: '0.5rem 0.75rem',
              background: '#fef2f2',
              border: '1px solid #fecaca',
              borderRadius: '6px',
              color: '#dc2626',
              fontSize: '0.875rem',
              display: 'flex',
              alignItems: 'center',
              gap: '0.5rem'
            }}>
              <span>⚠️</span>
              <span>{dateError}</span>
            </div>
          )}

          {campaignStatus && (
            <div style={{ 
              marginTop: '1rem',
              padding: '0.75rem 1rem',
              background: `${campaignStatus.color}15`,
              border: `1px solid ${campaignStatus.color}40`,
              borderRadius: '8px',
              display: 'flex',
              alignItems: 'center',
              gap: '0.75rem'
            }}>
              <span style={{ 
                display: 'inline-block',
                width: '8px',
                height: '8px',
                borderRadius: '50%',
                background: campaignStatus.color
              }}></span>
              <span style={{ color: campaignStatus.color, fontWeight: '600' }}>
                Campaign is {campaignStatus.label}
              </span>
              <span style={{ color: '#64748b', fontSize: '0.875rem' }}>
                {formatDate(formData.campaignStart)} - {formatDate(formData.campaignEnd)}
              </span>
            </div>
          )}
        </div>

        <div className="form-section">
          <div className="form-section-title">Receipt Date Validity</div>
          <p style={{ color: '#64748b', fontSize: '0.875rem', margin: '0 0 1rem 0' }}>
            Configure the buffer period for acceptable receipt dates outside the campaign period.
          </p>
          
          <div className="form-group">
            <label>Receipt Date Buffer</label>
            <div className="input-with-unit">
              <input
                type="text"
                inputMode="numeric"
                pattern="[0-9]*"
                value={formData.receiptDateBuffer}
                onChange={(e) => handleBufferChange(e.target.value)}
                placeholder="0"
                style={{ paddingRight: '3.5rem' }}
              />
              <span className="input-unit">days</span>
            </div>
            <span className="form-hint">
              Receipts dated within this many days before or after the campaign period will be accepted.
            </span>
          </div>
        </div>

        <div className="form-section" style={{ background: '#eff6ff', border: '1px solid #93c5fd' }}>
          <div style={{ display: 'flex', gap: '0.75rem', alignItems: 'flex-start' }}>
            <span style={{ fontSize: '1.25rem' }}>📅</span>
            <div>
              <strong style={{ color: '#1d4ed8' }}>Receipt Date Range</strong>
              {formData.campaignStart && formData.campaignEnd ? (
                <p style={{ color: '#2563eb', fontSize: '0.875rem', margin: '0.5rem 0 0 0' }}>
                  With a {formData.receiptDateBuffer}-day buffer, receipts dated between{' '}
                  <strong>
                    {formatDate(new Date(new Date(formData.campaignStart).getTime() - formData.receiptDateBuffer * 24 * 60 * 60 * 1000).toISOString().split('T')[0])}
                  </strong>{' '}
                  and{' '}
                  <strong>
                    {formatDate(new Date(new Date(formData.campaignEnd).getTime() + formData.receiptDateBuffer * 24 * 60 * 60 * 1000).toISOString().split('T')[0])}
                  </strong>{' '}
                  will be accepted.
                </p>
              ) : (
                <p style={{ color: '#2563eb', fontSize: '0.875rem', margin: '0.5rem 0 0 0' }}>
                  Set campaign start and end dates to see the valid receipt date range.
                </p>
              )}
            </div>
          </div>
        </div>

        <div className="form-section">
          <div className="form-section-title">Time Rules Summary</div>
          <table style={{ width: '100%', borderCollapse: 'collapse', fontSize: '0.875rem' }}>
            <tbody>
              <tr style={{ borderBottom: '1px solid #e2e8f0' }}>
                <td style={{ padding: '0.75rem', color: '#64748b', width: '40%' }}>Campaign Start</td>
                <td style={{ padding: '0.75rem', fontWeight: '500' }}>{formatDate(formData.campaignStart)}</td>
              </tr>
              <tr style={{ borderBottom: '1px solid #e2e8f0' }}>
                <td style={{ padding: '0.75rem', color: '#64748b' }}>Campaign End</td>
                <td style={{ padding: '0.75rem', fontWeight: '500' }}>{formatDate(formData.campaignEnd)}</td>
              </tr>
              <tr style={{ borderBottom: '1px solid #e2e8f0' }}>
                <td style={{ padding: '0.75rem', color: '#64748b' }}>Receipt Buffer</td>
                <td style={{ padding: '0.75rem', fontWeight: '500' }}>±{formData.receiptDateBuffer} days</td>
              </tr>
              <tr>
                <td style={{ padding: '0.75rem', color: '#64748b' }}>Status</td>
                <td style={{ padding: '0.75rem' }}>
                  {campaignStatus ? (
                    <span style={{ 
                      padding: '0.25rem 0.75rem',
                      background: `${campaignStatus.color}20`,
                      color: campaignStatus.color,
                      borderRadius: '20px',
                      fontWeight: '600',
                      fontSize: '0.8125rem'
                    }}>
                      {campaignStatus.label}
                    </span>
                  ) : (
                    <span style={{ color: '#94a3b8' }}>Not configured</span>
                  )}
                </td>
              </tr>
            </tbody>
          </table>
        </div>

        {message && (
          <div className={`status-message ${message.type}`}>
            {message.type === 'success' ? '✓' : '✕'} {message.text}
          </div>
        )}

        {!hideWizardSaveBar && (
          <div className="save-bar">
            <button 
              className="btn-save" 
              onClick={handleSave}
              disabled={saving || !isDirty}
            >
              {saving ? 'Saving...' : 'Save Changes'}
            </button>
          </div>
        )}
      </div>
    </div>
  );
});

export default TimeRulesTab;
